import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import styled from '@emotion/styled';
import { SearchX, X } from 'lucide-react';
import {
  ModalOverlay, ModalDialog, ModalTitle, ModalBody, ModalActions,
  PrimaryButton, SecondaryButton, DestructiveButton, Skeleton, SkeletonRow,
} from './styled-components';
import { type Toast, useFetchData, useToastBus } from './hooks';

// ---------------------------------------------------------------------------
// ConfirmModal
// ---------------------------------------------------------------------------

interface ConfirmModalProps {
  title: string;
  body: string;
  confirmLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  title, body, confirmLabel = 'Confirm', destructive = false, loading = false, onConfirm, onCancel,
}: ConfirmModalProps) {
  return (
    <ModalOverlay onClick={onCancel}>
      <ModalDialog onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{title}</ModalTitle>
        <ModalBody>{body}</ModalBody>
        <ModalActions>
          <SecondaryButton onClick={onCancel} disabled={loading}>Cancel</SecondaryButton>
          {destructive
            ? <DestructiveButton onClick={onConfirm} disabled={loading}>{loading ? 'Working…' : confirmLabel}</DestructiveButton>
            : <PrimaryButton onClick={onConfirm} disabled={loading}>{loading ? 'Working…' : confirmLabel}</PrimaryButton>
          }
        </ModalActions>
      </ModalDialog>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------

const EmptyStateWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '64px 0',
  gap: 12,
  color: 'var(--text-secondary)',
  textAlign: 'center',
});

const EmptyIcon = styled.div({
  color: 'var(--accent-cyan)',
  opacity: 0.4,
  marginBottom: 8,
});

const EmptyTitle = styled.div({ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' });
const EmptyBody = styled.div({ fontSize: 14, color: 'var(--text-secondary)', maxWidth: 320 });

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  body?: string;
  action?: { label: string; onClick: () => void };
}

export function EmptyState({ icon, title, body, action }: EmptyStateProps) {
  return (
    <EmptyStateWrapper>
      <EmptyIcon>{icon}</EmptyIcon>
      <EmptyTitle>{title}</EmptyTitle>
      {body && <EmptyBody>{body}</EmptyBody>}
      {action && (
        <PrimaryButton style={{ marginTop: 8 }} onClick={action.onClick}>
          {action.label}
        </PrimaryButton>
      )}
    </EmptyStateWrapper>
  );
}

// ---------------------------------------------------------------------------
// ResourceNotFound
// ---------------------------------------------------------------------------

interface ResourceNotFoundProps {
  label: string;
  backTo: string;
}

// Detail endpoints filter on the session's active org, so a shared link most
// often 404s because the reader is in a different org than the sender. Name the
// org we searched rather than leaving them with a bare "not found".
export function ResourceNotFound({ label, backTo }: ResourceNotFoundProps) {
  const navigate = useNavigate();
  const { data: me } = useFetchData<{ org_name: string }>('/dashboard/api/me', { org_name: '' });
  const { data: orgs } = useFetchData<unknown[]>('/dashboard/api/me/orgs', []);

  return (
    <EmptyState
      icon={<SearchX size={40} />}
      title={me.org_name ? `${label} not found in ${me.org_name}` : `${label} not found`}
      body={orgs.length > 1
        ? 'If the link came from another organization, switch organizations in the sidebar — you will stay on this page.'
        : 'It may have been deleted, or the link may belong to an organization you do not have access to.'}
      action={{ label: `Back to ${backTo.slice(1)}`, onClick: () => navigate(backTo) }}
    />
  );
}

// ---------------------------------------------------------------------------
// Toast container
// ---------------------------------------------------------------------------

const ToastContainer = styled.div({
  position: 'fixed',
  top: 20,
  right: 20,
  zIndex: 2000,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const ToastItem = styled.div<{ variant: Toast['variant'] }>(({ variant }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
  boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
  animation: 'pageIn 250ms cubic-bezier(0.4,0,0.2,1)',
  minWidth: 280,
  ...(variant === 'success' && { background: '#0e2b1f', color: 'var(--accent-green)', border: '1px solid rgba(0,200,117,0.25)' }),
  ...(variant === 'error' && { background: '#2b0f0f', color: 'var(--accent-red)', border: '1px solid rgba(224,80,80,0.25)' }),
  ...(variant === 'info' && { background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--bg-border)' }),
}));

export function ToastHost() {
  const { toasts, dismiss } = useToastBus();
  if (!toasts.length) return null;
  return (
    <ToastContainer>
      {toasts.map((t) => (
        <ToastItem key={t.id} variant={t.variant}>
          <span style={{ flex: 1 }}>{t.message}</span>
          <GhostX onClick={() => dismiss(t.id)}><X size={14} /></GhostX>
        </ToastItem>
      ))}
    </ToastContainer>
  );
}

const GhostX = styled.button({
  background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6,
  padding: 2, display: 'flex', '&:hover': { opacity: 1 },
});

// ---------------------------------------------------------------------------
// Skeleton table rows
// ---------------------------------------------------------------------------

export function SkeletonRows({ cols, rows = 5 }: { cols: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => i).map((i) => (
        <SkeletonRow key={i}>
          {Array.from({ length: cols }, (_, j) => j).map((j) => (
            <td key={j}>
              <Skeleton height={14} width={j === 0 ? '60%' : '80%'} />
            </td>
          ))}
        </SkeletonRow>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Waveform canvas (sign-in background)
// ---------------------------------------------------------------------------

const WaveCanvas = styled.canvas({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  filter: 'blur(3px)',
});

interface WaveDef {
  yBase: number;    // vertical center as fraction of height
  freq: number;     // cycles across full width
  baseAmp: number;  // base amplitude as fraction of height
  speed: number;    // horizontal scroll speed
  phase: number;    // initial phase offset
  alpha: number;    // opacity
  color: string;
  lw: number;       // line width
  pRate: number;    // parallax drift rate (signed → direction)
  pAmp: number;     // parallax drift amplitude
  glow: boolean;    // draw with shadowBlur glow
}

const WAVES: WaveDef[] = [
  // Deep background — wide, slow
  { yBase: 0.50, freq: 1.3, baseAmp: 0.20, speed: 1.8,  phase: 0.0, alpha: 0.05, color: '#0060a0', lw: 3.0, pRate:  0.18, pAmp: 0.04, glow: false },
  { yBase: 0.50, freq: 2.0, baseAmp: 0.16, speed: 2.4,  phase: 2.5, alpha: 0.06, color: '#0080b8', lw: 2.5, pRate: -0.16, pAmp: 0.04, glow: false },
  { yBase: 0.48, freq: 1.6, baseAmp: 0.18, speed: 1.3,  phase: 5.1, alpha: 0.04, color: '#004880', lw: 3.5, pRate:  0.10, pAmp: 0.05, glow: false },

  // Mid — primary glowing lines
  { yBase: 0.42, freq: 3.5, baseAmp: 0.13, speed: 4.8,  phase: 1.1, alpha: 0.22, color: '#00c4e8', lw: 1.5, pRate:  0.45, pAmp: 0.06, glow: true  },
  { yBase: 0.58, freq: 3.2, baseAmp: 0.12, speed: 4.2,  phase: 4.3, alpha: 0.20, color: '#00c4e8', lw: 1.5, pRate: -0.40, pAmp: 0.06, glow: true  },
  { yBase: 0.50, freq: 2.8, baseAmp: 0.10, speed: 3.6,  phase: 3.0, alpha: 0.14, color: '#00aad0', lw: 1.2, pRate:  0.22, pAmp: 0.05, glow: true  },

  // Upper / lower harmonics
  { yBase: 0.32, freq: 5.5, baseAmp: 0.09, speed: 8.5,  phase: 2.4, alpha: 0.14, color: '#20d8ff', lw: 1.0, pRate:  0.75, pAmp: 0.05, glow: false },
  { yBase: 0.68, freq: 5.0, baseAmp: 0.09, speed: 7.5,  phase: 5.9, alpha: 0.13, color: '#20d8ff', lw: 1.0, pRate: -0.65, pAmp: 0.05, glow: false },
  { yBase: 0.36, freq: 6.2, baseAmp: 0.07, speed: 10.0, phase: 0.8, alpha: 0.10, color: '#40e0ff', lw: 0.8, pRate:  0.90, pAmp: 0.04, glow: false },
  { yBase: 0.64, freq: 5.8, baseAmp: 0.07, speed: 9.0,  phase: 3.7, alpha: 0.09, color: '#40e0ff', lw: 0.8, pRate: -0.82, pAmp: 0.04, glow: false },

  // Fringe — near edges
  { yBase: 0.18, freq: 4.0, baseAmp: 0.06, speed: 3.5,  phase: 1.8, alpha: 0.07, color: '#0090b8', lw: 1.0, pRate:  0.30, pAmp: 0.03, glow: false },
  { yBase: 0.82, freq: 3.7, baseAmp: 0.06, speed: 3.0,  phase: 4.9, alpha: 0.07, color: '#0090b8', lw: 1.0, pRate: -0.28, pAmp: 0.03, glow: false },
  { yBase: 0.08, freq: 3.2, baseAmp: 0.04, speed: 2.2,  phase: 0.5, alpha: 0.04, color: '#007090', lw: 1.0, pRate:  0.15, pAmp: 0.02, glow: false },
  { yBase: 0.92, freq: 3.0, baseAmp: 0.04, speed: 2.0,  phase: 6.0, alpha: 0.04, color: '#007090', lw: 1.0, pRate: -0.14, pAmp: 0.02, glow: false },
];

// Voice-cadence envelope. Each wave passes its own phase offset so they
// peak at different times — organic, not synchronized.
function voiceEnvelope(t: number): number {
  const syllable = Math.max(0, Math.sin(t * 15.7)) ** 1.6;       // ~3 Hz syllables
  const micro    = Math.max(0, Math.sin(t * 26.2 + 1.1)) ** 2.2; // ~5 Hz micro-bursts
  const phrase   = Math.max(0, Math.sin(t * 9.4  + 3.0)) ** 0.8; // ~1.8 Hz phrase swell
  return 0.12 + 0.88 * Math.max(syllable, micro * 0.60, phrase * 0.45);
}

export function WaveformBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let frame: number;
    let t = 0;
    let lastTs: number | null = null;

    const drawWave = (w: WaveDef) => {
      const { width, height } = canvas;
      const yPos = w.yBase + Math.sin(t * w.pRate + w.phase * 0.4) * w.pAmp;
      const amp  = w.baseAmp * voiceEnvelope(t + w.phase * 0.06);

      ctx.save();
      ctx.globalAlpha = w.alpha;
      ctx.strokeStyle = w.color;
      ctx.lineWidth = w.lw;
      ctx.lineJoin = 'round';
      if (w.glow) { ctx.shadowColor = w.color; ctx.shadowBlur = 12; }

      ctx.beginPath();
      const step = 2;
      for (let x = 0; x <= width; x += step) {
        const norm = x / width;
        const y = height * yPos
          + Math.sin(norm * Math.PI * 2 * w.freq + t * w.speed + w.phase)
            * height * amp;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();
    };

    const draw = (ts: number) => {
      if (lastTs) {
        const dt = (ts - lastTs) / 1000;
        t += dt * 1.2;
      }
      lastTs = ts;
      
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);
      WAVES.forEach((w) => { drawWave(w); });
      frame = requestAnimationFrame(draw);
    };

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resize();
    frame = requestAnimationFrame(draw);
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', resize); };
  }, []);

  return <WaveCanvas ref={canvasRef} />;
}

// ---------------------------------------------------------------------------
// KPI Sparkline
// ---------------------------------------------------------------------------

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
}

export function Sparkline({ data, width = 80, height = 28 }: SparklineProps) {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const barW = Math.floor((width - (data.length - 1) * 2) / data.length);

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {data.map((v, i) => ({ v, i })).map(({ v, i }) => {
        const barH = Math.max(3, (v / max) * (height - 4));
        const x = i * (barW + 2);
        const y = height - barH;
        const isLast = i === data.length - 1;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={barH}
            rx={2}
            fill={isLast ? '#33d4f0' : '#00c4e8'}
            style={{
              transformOrigin: `${x}px ${height}px`,
              animation: `cardIn 400ms ease ${i * 50}ms both`,
            }}
          />
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Pagination controls
// ---------------------------------------------------------------------------

const PaginationBar = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginTop: 16,
  color: 'var(--text-secondary)',
  fontSize: 13,
});

interface PaginationProps {
  page: number;
  total: number;
  limit: number;
  onChange: (page: number) => void;
}

export function Pagination({ page, total, limit, onChange }: PaginationProps) {
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const hasNext = page * limit < total;

  return (
    <PaginationBar>
      <SecondaryButton style={{ padding: '5px 12px', fontSize: 13 }} disabled={page <= 1} onClick={() => onChange(page - 1)}>Prev</SecondaryButton>
      <span>{from}–{to} of {total}</span>
      <SecondaryButton style={{ padding: '5px 12px', fontSize: 13 }} disabled={!hasNext} onClick={() => onChange(page + 1)}>Next</SecondaryButton>
    </PaginationBar>
  );
}

interface CursorPaginationProps {
  page: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

/** Prev/Next controls for cursor-paginated endpoints, which don't report a total. */
export function CursorPagination({ page, hasPrev, hasNext, onPrev, onNext }: CursorPaginationProps) {
  return (
    <PaginationBar>
      <SecondaryButton style={{ padding: '5px 12px', fontSize: 13 }} disabled={!hasPrev} onClick={onPrev}>Prev</SecondaryButton>
      <span>Page {page}</span>
      <SecondaryButton style={{ padding: '5px 12px', fontSize: 13 }} disabled={!hasNext} onClick={onNext}>Next</SecondaryButton>
    </PaginationBar>
  );
}

// ---------------------------------------------------------------------------
// Direction filter (all / inbound / outbound)
// ---------------------------------------------------------------------------

const DirectionToggleWrap = styled.div({
  display: 'flex',
  background: 'var(--bg-surface)',
  border: '1px solid var(--bg-border)',
  borderRadius: 6,
  overflow: 'hidden',
});

const DirBtn = styled.button<{ active?: boolean }>(({ active }) => ({
  background: active ? 'var(--accent-cyan-glow)' : 'none',
  border: 'none',
  padding: '7px 14px',
  fontSize: 13,
  color: active ? 'var(--accent-cyan)' : 'var(--text-secondary)',
  cursor: 'pointer',
  fontWeight: active ? 600 : 400,
}));

export type DirectionFilterValue = 'all' | 'inbound' | 'outbound';

interface DirectionFilterProps {
  value: DirectionFilterValue;
  onChange: (v: DirectionFilterValue) => void;
}

export function DirectionFilter({ value, onChange }: DirectionFilterProps) {
  const options: DirectionFilterValue[] = ['all', 'inbound', 'outbound'];
  return (
    <DirectionToggleWrap>
      {options.map((d) => (
        <DirBtn key={d} active={value === d} onClick={() => onChange(d)}>
          {d.charAt(0).toUpperCase() + d.slice(1)}
        </DirBtn>
      ))}
    </DirectionToggleWrap>
  );
}

// ---------------------------------------------------------------------------
// Multi-select dropdown filter
// ---------------------------------------------------------------------------

const DropdownWrap = styled.div({ position: 'relative', display: 'inline-block' });

const DropdownBtn = styled.button<{ active?: boolean }>(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  background: active ? 'var(--accent-cyan-glow)' : 'none',
  border: '1px solid var(--bg-border)',
  borderRadius: 6,
  padding: '8px 12px',
  fontSize: 14,
  color: active ? 'var(--accent-cyan)' : 'var(--text-secondary)',
  cursor: 'pointer',
  fontWeight: 400,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
}));

const DropdownMenu = styled.div({
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  zIndex: 100,
  background: 'var(--bg-surface)',
  border: '1px solid var(--bg-border)',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
  padding: '6px 0',
  minWidth: 180,
  maxHeight: 280,
  overflowY: 'auto',
});

const DropdownItem = styled.label({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 14px',
  fontSize: 13,
  color: 'var(--text-primary)',
  cursor: 'pointer',
  '&:hover': { background: 'var(--bg-surface-hover)' },
});

const Checkbox = styled.input({
  accentColor: 'var(--accent-cyan)',
  cursor: 'pointer',
});

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: MultiSelectOption[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}

export function MultiSelectFilter({ label, options, selected, onChange }: MultiSelectFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [pending, setPending] = React.useState(selected);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setPending(selected); }, [selected]);

  const close = () => {
    setOpen(false);
    onChange(pending);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  const toggle = (value: string) => {
    const next = new Set(pending);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setPending(next);
  };

  const count = selected.size;

  return (
    <DropdownWrap ref={wrapRef}>
      <DropdownBtn active={count > 0} onClick={() => open ? close() : setOpen(true)}>
        {label}
        <span style={{ fontSize: 10, display: 'inline-block', transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
      </DropdownBtn>
      {open && (
        <DropdownMenu>
          {options.map((o) => (
            <DropdownItem key={o.value}>
              <Checkbox
                type="checkbox"
                checked={pending.has(o.value)}
                onChange={() => toggle(o.value)}
              />
              {o.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      )}
    </DropdownWrap>
  );
}

// ---------------------------------------------------------------------------
// Styled checkbox box (custom box with checkmark; caller supplies the label/click target)
// ---------------------------------------------------------------------------

export const StyledCheckboxBox = styled.span<{ checked?: boolean; hasError?: boolean; disabled?: boolean }>(({ checked, hasError, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  flexShrink: 0,
  marginTop: 1,
  borderRadius: 4,
  border: `1.5px solid ${hasError && !checked ? 'var(--accent-red)' : checked ? 'var(--accent-cyan)' : 'var(--bg-border)'}`,
  background: checked ? 'var(--accent-cyan)' : 'var(--bg-base)',
  transition: 'border-color 150ms ease, background 150ms ease',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  '&::after': checked ? {
    content: '""',
    display: 'block',
    width: 4,
    height: 8,
    borderRight: '2px solid var(--bg-base)',
    borderBottom: '2px solid var(--bg-base)',
    transform: 'rotate(45deg)',
    marginTop: -2,
  } : {},
}));

// ---------------------------------------------------------------------------
// Copy-to-clipboard button
// ---------------------------------------------------------------------------

const CopyBtn = styled.button<{ color?: string; size?: number }>(({ color, size }) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: color ?? 'var(--text-muted)',
  padding: '2px 4px',
  borderRadius: 3,
  fontSize: size,
  lineHeight: 1,
  transition: 'color 150ms, transform 150ms',
  '&:hover': { color: 'var(--accent-cyan)' },
  '&:active': { transform: 'scale(0.9)' },
}));

export function CopyButton({ text, color, size }: { text: string; color?: string; size?: number }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <CopyBtn onClick={handleCopy} title="Copy" color={color} size={size}>
      {copied ? '✓' : '⎘'}
    </CopyBtn>
  );
}
